import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { blogPosts } from "@/mocks/blog";

export default function LatestBlog() {
  const cardsRef = useRef<HTMLDivElement>(null);
  const latestPosts = blogPosts.slice(0, 3);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("opacity-100", "translate-y-0");
            entry.target.classList.remove("opacity-0", "translate-y-8");
          }
        });
      },
      { threshold: 0.1 }
    );

    const cards = cardsRef.current?.children;
    if (cards) {
      Array.from(cards).forEach((card, index) => {
        card.classList.add("opacity-0", "translate-y-8", "transition-all", "duration-700");
        (card as HTMLElement).style.transitionDelay = `${index * 150}ms`;
        observer.observe(card);
      });
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="blog" className="py-20 md:py-28 bg-dark-50">
      <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
            <div>
              <span className="inline-block text-dark-400 text-xs font-semibold uppercase tracking-widest mb-4">
                Blog
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-dark-900 leading-tight">
                Stručni saveti i vodiči
              </h2>
            </div>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-brand-600 hover:text-brand-700 text-sm font-semibold transition-colors whitespace-nowrap"
            >
              Svi članci
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          <div ref={cardsRef} className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {latestPosts.map((post) => (
              <Link
                key={post.slug}
                to={`/blog/${post.slug}`}
                className="group bg-white rounded-2xl border border-dark-100 overflow-hidden hover:-translate-y-2 transition-all duration-300"
              >
                <div className="aspect-[16/10] bg-dark-100 overflow-hidden relative">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <span className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm text-dark-800 text-xs font-medium px-3 py-1.5 rounded-full">
                    {post.category}
                  </span>
                </div>
                <div className="p-5 md:p-6">
                  <div className="flex items-center gap-3 text-xs text-dark-400 mb-3">
                    <span className="flex items-center gap-1">
                      <i className="ri-calendar-line"></i>
                      {post.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line"></i>
                      {post.readTime}
                    </span>
                  </div>
                  <h3 className="text-base font-bold text-dark-900 mb-3 group-hover:text-brand-600 transition-colors line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-dark-500 text-sm leading-relaxed line-clamp-2">
                    {post.excerpt}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}